import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  makeStyles,
  useTheme
} from '@material-ui/core';
import {
  Settings as SettingsIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon,
  ShoppingCart as ShoppingCartIcon,
  Info as InfoIcon
} from '@material-ui/icons';

import { i18n } from '../translate/i18n';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    textAlign: 'center',
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    fontSize: '4rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  subtitle: {
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
    maxWidth: 600,
    lineHeight: 1.6,
  },
  card: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    textAlign: 'left',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  cardIcon: {
    fontSize: '2.5rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  cardDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  configButton: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    fontSize: '1.1rem',
    fontWeight: 600,
  },
  featuresGrid: {
    marginTop: theme.spacing(4),
    maxWidth: 800,
    width: '100%',
  },
  infoCard: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
    border: `1px solid ${theme.palette.primary.main}30`,
    marginBottom: theme.spacing(3),
  },
}));

const NappNotConfigured = ({ pageType = 'general' }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const handleConfigureClick = () => {
    history.push('/settings');
    // Scroll para a aba NAPP ou ativar a aba NAPP
    setTimeout(() => {
      const nappTab = document.querySelector('[value="napp"]');
      if (nappTab) {
        nappTab.click();
      }
    }, 500);
  };

  const getPageSpecificContent = () => {
    switch (pageType) {
      case 'orders':
        return {
          icon: <ShoppingCartIcon className={classes.icon} />,
          title: i18n.t('napp.notConfigured.orders.title', 'Pedidos E-commerce Não Configurados'),
          subtitle: i18n.t('napp.notConfigured.orders.subtitle', 'Para visualizar e gerenciar seus pedidos de e-commerce, você precisa configurar a integração com a NAPP Solutions.')
        };
      case 'analytics':
        return {
          icon: <AssessmentIcon className={classes.icon} />,
          title: i18n.t('napp.notConfigured.analytics.title', 'Analytics de Pedidos Não Disponível'),
          subtitle: i18n.t('napp.notConfigured.analytics.subtitle', 'Para acessar as análises avançadas e métricas de performance dos seus pedidos, configure primeiro a integração NAPP.')
        };
      case 'sales':
        return {
          icon: <StoreIcon className={classes.icon} />,
          title: i18n.t('napp.notConfigured.sales.title', 'Central de Vendas Não Configurada'),
          subtitle: i18n.t('napp.notConfigured.sales.subtitle', 'Para acessar o dashboard executivo e análises avançadas de vendas, você precisa configurar a integração com a NAPP Solutions.')
        };
      default:
        return {
          icon: <StoreIcon className={classes.icon} />,
          title: i18n.t('napp.notConfigured.general.title', 'Integração NAPP Não Configurada'),
          subtitle: i18n.t('napp.notConfigured.general.subtitle', 'Para acessar os recursos de e-commerce, você precisa configurar a integração com a NAPP Solutions.')
        };
    }
  };

  const { icon, title, subtitle } = getPageSpecificContent();

  const features = [
    {
      icon: <ShoppingCartIcon className={classes.cardIcon} />,
      title: i18n.t('napp.features.orders.title', 'Gestão de Pedidos'),
      description: i18n.t('napp.features.orders.description', 'Sincronize e gerencie pedidos de todas as suas plataformas de e-commerce em um só lugar.')
    },
    {
      icon: <AssessmentIcon className={classes.cardIcon} />,
      title: i18n.t('napp.features.analytics.title', 'Analytics Avançado'),
      description: i18n.t('napp.features.analytics.description', 'Acompanhe métricas detalhadas, KPIs e performance de vendas com dashboards interativos.')
    },
    {
      icon: <StoreIcon className={classes.cardIcon} />,
      title: i18n.t('napp.features.sales.title', 'Central de Vendas'),
      description: i18n.t('napp.features.sales.description', 'Dashboard executivo com análise comparativa entre plataformas e relatórios avançados.')
    }
  ];

  return (
    <Paper className={classes.root} elevation={0}>
      {icon}

      <Typography variant="h4" className={classes.title}>
        {title}
      </Typography>

      <Typography variant="body1" className={classes.subtitle}>
        {subtitle}
      </Typography>

      {/* Card informativo */}
      <Card className={classes.infoCard}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <InfoIcon style={{ marginRight: 8, color: theme.palette.primary.main }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {i18n.t('napp.notConfigured.info.title', 'Como Configurar')}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {i18n.t('napp.notConfigured.info.description', 'A integração NAPP Solutions permite sincronizar pedidos de marketplaces como iFood, Americanas, Magazine Luiza e outros. Você receberá uma chave de API da NAPP para configurar a conexão.')}
          </Typography>
        </CardContent>
      </Card>

      {/* Grid de recursos disponíveis */}
      <Grid container spacing={3} className={classes.featuresGrid}>
        {features.map((feature, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card className={classes.card}>
              <CardContent>
                {feature.icon}
                <Typography variant="h6" className={classes.cardTitle}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" className={classes.cardDescription}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Botão de configuração */}
      <Button
        variant="contained"
        color="primary"
        size="large"
        className={classes.configButton}
        startIcon={<SettingsIcon />}
        onClick={handleConfigureClick}
      >
        {i18n.t('napp.notConfigured.configureButton', 'Configurar Integração NAPP')}
      </Button>

      <Box mt={2}>
        <Typography variant="caption" color="textSecondary">
          {i18n.t('napp.notConfigured.support', 'Precisa de ajuda? Entre em contato com o suporte da NAPP Solutions')}
        </Typography>
      </Box>
    </Paper>
  );
};

export default NappNotConfigured;